/**
 * The twelve profile sections, in page order, with A2's ids
 * (`profile/section-meta.ts`, `PROFILE_SECTIONS` in `data/mock-plus.ts`).
 * The ids are the `?section=` values and what search (T66) indexes, so they
 * never change. Each has an icon, a title and one honest "why": the first
 * eight are questions the plan is built from (`asks`), the last four say what
 * the section is instead.
 */
import {
  "profile.section_account" as sectionAccount,
  "profile.section_app" as sectionApp,
  "profile.section_body" as sectionBody,
  "profile.section_equipment" as sectionEquipment,
  "profile.section_exclusions" as sectionExclusions,
  "profile.section_goal" as sectionGoal,
  "profile.section_health" as sectionHealth,
  "profile.section_lifestyle" as sectionLifestyle,
  "profile.section_notifications" as sectionNotifications,
  "profile.section_pain" as sectionPain,
  "profile.section_routine" as sectionRoutine,
  "profile.section_you" as sectionYou,
  "profile.why_account" as whyAccount,
  "profile.why_app" as whyApp,
  "profile.why_body" as whyBody,
  "profile.why_equipment" as whyEquipment,
  "profile.why_exclusions" as whyExclusions,
  "profile.why_goal" as whyGoal,
  "profile.why_health" as whyHealth,
  "profile.why_lifestyle" as whyLifestyle,
  "profile.why_notifications" as whyNotifications,
  "profile.why_pain" as whyPain,
  "profile.why_routine" as whyRoutine,
  "profile.why_you" as whyYou,
} from "@repo/i18n";
import {
  Ban,
  BedDouble,
  Bell,
  Bone,
  CalendarDays,
  Dumbbell,
  HeartPulse,
  type LucideIcon,
  Palette,
  Scale,
  Target,
  UserCog,
  UserRound,
} from "lucide-react";

export const SECTION_IDS = [
  "you",
  "body",
  "pain",
  "goal",
  "routine",
  "equipment",
  "lifestyle",
  "health",
  "exclusions",
  "notifications",
  "app",
  "account",
] as const;

export type SectionId = (typeof SECTION_IDS)[number];

const IDS = new Set<string>(SECTION_IDS);

export function isSectionId(value: unknown): value is SectionId {
  return typeof value === "string" && IDS.has(value);
}

export interface SectionMeta {
  /** A question the plan is built from: the "why" is prefixed "Por que perguntamos:". */
  readonly asks: boolean;
  readonly icon: LucideIcon;
  /** Called at render, so the active locale is read each time. */
  readonly title: () => string;
  readonly why: () => string;
}

export const SECTION_META: Readonly<Record<SectionId, SectionMeta>> = {
  you: { asks: true, icon: UserRound, title: sectionYou, why: whyYou },
  body: { asks: true, icon: Scale, title: sectionBody, why: whyBody },
  pain: { asks: true, icon: Bone, title: sectionPain, why: whyPain },
  goal: { asks: true, icon: Target, title: sectionGoal, why: whyGoal },
  routine: {
    asks: true,
    icon: CalendarDays,
    title: sectionRoutine,
    why: whyRoutine,
  },
  equipment: {
    asks: true,
    icon: Dumbbell,
    title: sectionEquipment,
    why: whyEquipment,
  },
  lifestyle: {
    asks: true,
    icon: BedDouble,
    title: sectionLifestyle,
    why: whyLifestyle,
  },
  health: {
    asks: true,
    icon: HeartPulse,
    title: sectionHealth,
    why: whyHealth,
  },
  exclusions: {
    asks: false,
    icon: Ban,
    title: sectionExclusions,
    why: whyExclusions,
  },
  notifications: {
    asks: false,
    icon: Bell,
    title: sectionNotifications,
    why: whyNotifications,
  },
  app: { asks: false, icon: Palette, title: sectionApp, why: whyApp },
  account: {
    asks: false,
    icon: UserCog,
    title: sectionAccount,
    why: whyAccount,
  },
};
